import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage,
  schema,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes("黑料吃瓜") ? title : `${title} - 黑料吃瓜`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
    }

    setMeta("og:title", ogTitle || fullTitle, true);
    setMeta("og:description", ogDescription || description, true);
    setMeta("og:type", ogType, true);
    if (canonicalUrl) setMeta("og:url", canonicalUrl, true);
    if (ogImage) setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle || fullTitle);
    setMeta("twitter:description", ogDescription || description);

    const schemaScripts: HTMLScriptElement[] = [];
    if (schema && schema.length > 0) {
      schema.forEach((s) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
        schemaScripts.push(script);
      });
    }

    return () => {
      schemaScripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogImage, schema]);

  return null;
}